import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { FileText, Link2, Reply, FilePlus2, Megaphone, CheckCheck, SmilePlus, Trash2, Ban, Pencil } from 'lucide-react';
import { API_URL, fmtTime, fileUrl } from './chatShared';
import { ChatAvatar } from './ChatAvatar';
import { UrgencyBadge, URGENCY } from './ChatUrgency';
import { ReactionBar, ReactionChips } from './ChatReactions';
import { EmojiText, onlyEmojis } from './EmojiText';

const Quote = ({ r }) => (
  <button type="button" onClick={() => document.getElementById(`chatmsg-${r.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
    className="block w-full text-left border-l-4 border-sky-500 bg-sky-100 text-slate-700 px-2 py-1 mb-1 text-xs rounded-md" data-testid="chat-msg-quote">
    <span className="font-semibold block text-sky-800">{r.sender_name}</span>
    <span className="line-clamp-2"><EmojiText text={r.text} />{!r.text && (r.file_name ? `Ficheiro: ${r.file_name}` : '…')}</span>
  </button>
);

const AnnouncementFooter = ({ m, me, canSeeAcks, onShowAcks, onChanged }) => {
  const isRecipient = (m.recipients || []).includes(me.id);
  const acked = !!(m.acks || {})[me.id];
  const ack = () => axios.post(`${API_URL}/chat/messages/${m.id}/ack`).then(() => { onChanged(); window.dispatchEvent(new Event('chat-ack')); })
    .catch(() => toast.error('Não foi possível confirmar a leitura'));
  return (
    <div className="mt-1.5 pt-1.5 border-t border-current/10 flex items-center gap-2 text-[11px]">
      {isRecipient && (acked
        ? <span className="flex items-center gap-1 text-emerald-600" data-testid={`chat-ack-done-${m.id}`}><CheckCheck className="w-3.5 h-3.5" />Leitura confirmada</span>
        : <button type="button" onClick={ack} className="px-2 py-1 rounded-full bg-emerald-600 text-white font-semibold hover:bg-emerald-700" data-testid={`chat-ack-btn-${m.id}`}>Li e confirmo</button>)}
      {canSeeAcks && (
        <button type="button" onClick={() => onShowAcks(m)} className="underline" data-testid={`chat-acks-count-${m.id}`}>
          Lido por {Object.keys(m.acks || {}).length}/{(m.recipients || []).length}
        </button>
      )}
    </div>
  );
};

const Actions = ({ m, onReply, onReact, onDelete, onEdit }) => (
  <span className="flex flex-col opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
    {onReact && <button type="button" onClick={onReact} title="Reagir" className="p-1 text-slate-400 hover:text-amber-500" data-testid={`chat-react-btn-${m.id}`}><SmilePlus className="w-4 h-4" /></button>}
    {onReply && <button type="button" onClick={() => onReply(m)} title="Responder" className="p-1 text-slate-400 hover:text-slate-700" data-testid={`chat-reply-btn-${m.id}`}><Reply className="w-4 h-4" /></button>}
    {onEdit && <button type="button" onClick={onEdit} title="Editar" className="p-1 text-slate-400 hover:text-sky-600" data-testid={`chat-edit-btn-${m.id}`}><Pencil className="w-4 h-4" /></button>}
    {onDelete && <button type="button" onClick={onDelete} title="Apagar" className="p-1 text-slate-400 hover:text-rose-600" data-testid={`chat-delete-btn-${m.id}`}><Trash2 className="w-4 h-4" /></button>}
  </span>
);

const EditHistory = ({ m, mine }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-1" data-testid={`chat-edit-history-${m.id}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} className={`text-[10px] underline ${mine ? 'text-amber-200' : 'text-amber-700'}`} data-testid={`chat-edit-history-toggle-${m.id}`}>
        {open ? 'Esconder original' : `Ver texto original (${m.edit_history.length} edição${m.edit_history.length > 1 ? 'ões' : ''})`}
      </button>
      {open && m.edit_history.map((h, i) => (
        <p key={i} className={`text-xs mt-1 rounded-md px-2 py-1 line-through decoration-1 ${mine ? 'bg-white/10 text-white/80' : 'bg-amber-50 text-slate-600'}`}>
          {h.text || '(sem texto)'} <span className="no-underline text-[10px] opacity-70">· alterado {fmtTime(h.at)}</span>
        </p>
      ))}
    </div>
  );
};

const DeletedPlaceholder = ({ m, mine }) => (
  <div id={`chatmsg-${m.id}`} data-testid={`chat-msg-${m.id}`} className={`flex ${mine ? 'justify-end' : 'justify-start pl-9'}`}>
    <p className="flex items-center gap-1.5 text-xs italic text-slate-400 bg-white/70 border border-dashed border-slate-200 rounded-2xl px-3 py-1.5" data-testid={`chat-msg-deleted-${m.id}`}>
      <Ban className="w-3.5 h-3.5" />{mine ? 'Apagou esta mensagem' : 'Esta mensagem foi apagada'} · {fmtTime(m.created_at)}
    </p>
  </div>
);

export const ChatBubble = (props) => (props.m.deleted && !props.m.deleted_by_name ? <DeletedPlaceholder m={props.m} mine={props.m.sender_id === props.me.id} /> : <Bubble {...props} />);

const Bubble = ({ m, me, avatarUrl, names, canSeeAcks, onReply, onUseFile, onShowAcks, onChanged, canResolve, onEdit }) => {
  const mine = m.sender_id === me.id;
  const [barOpen, setBarOpenRaw] = useState(false);
  const [below, setBelow] = useState(false);
  const press = useRef(null);
  const fired = useRef(false);
  const bubbleRef = useRef(null);
  const canReact = !!onReply && !m.deleted;
  const edit = mine && canReact && onEdit && m.text ? () => onEdit(m) : null;
  const del = mine && canReact ? () => {
    if (!window.confirm('Apagar esta mensagem?')) return;
    axios.delete(`${API_URL}/chat/messages/${m.id}`).then(() => { toast.success('Mensagem apagada'); onChanged(); }).catch((e) => toast.error(e?.response?.data?.detail || 'Erro ao apagar'));
  } : null;
  const setBarOpen = (v) => {
    const val = typeof v === 'function' ? v(barOpen) : v;
    if (val && bubbleRef.current) {
      const box = bubbleRef.current.closest('.overflow-y-auto')?.getBoundingClientRect();
      setBelow(!!box && bubbleRef.current.getBoundingClientRect().top - box.top < 56);
    }
    setBarOpenRaw(val);
  };
  const startPress = () => { fired.current = false; if (canReact) press.current = setTimeout(() => { fired.current = true; setBarOpen(true); }, 450); };
  const endPress = (e) => { clearTimeout(press.current); if (fired.current && e?.type === 'touchend') e.preventDefault(); };
  return (
    <div id={`chatmsg-${m.id}`} data-testid={`chat-msg-${m.id}`}>
      <div className={`group flex items-end gap-1.5 ${mine ? 'justify-end' : 'justify-start'}`}>
        {!mine && <ChatAvatar name={m.sender_name} url={avatarUrl} className="h-7 w-7 mb-1" testId={`chat-msg-avatar-${m.id}`} />}
        {mine && <Actions m={m} onReply={canReact ? onReply : null} onReact={canReact ? () => setBarOpen((o) => !o) : null} onDelete={del} onEdit={edit} />}
        <div ref={bubbleRef} onTouchStart={startPress} onTouchEnd={endPress} onTouchMove={endPress} onContextMenu={(e) => { if (canReact && window.matchMedia('(pointer: coarse)').matches) e.preventDefault(); }}
          className={`relative max-w-[80%] rounded-2xl px-3 py-2 text-sm shadow-sm select-text ${m.deleted ? 'border-2 border-dashed !border-rose-300' : ''} ${m.urgent_open ? URGENCY[m.urgency]?.ring || '' : ''} ${m.announcement ? 'bg-amber-50 text-slate-800 border border-amber-200' : mine ? 'bg-[#2D3E6F] text-white rounded-br-sm' : 'bg-white text-slate-800 rounded-bl-sm border border-slate-100'}`}
          data-testid={m.deleted ? `chat-msg-faded-${m.id}` : undefined}>
          {barOpen && (
            <>
              <div className="fixed inset-0 z-[60]" onClick={() => setBarOpen(false)} />
              <ReactionBar m={m} mine={mine} below={below} onClose={() => setBarOpen(false)} onChanged={onChanged} onReply={() => onReply(m)} onDelete={del} onEdit={edit} />
            </>
          )}
        {m.deleted && <p className="text-[10px] font-bold uppercase text-rose-600 mb-1 flex items-center gap-1" data-testid={`chat-deleted-info-${m.id}`}><Ban className="w-3 h-3" />Apagada por {m.deleted_by_name} · {fmtTime(m.deleted_at)}</p>}
        <div className={m.deleted ? 'opacity-50 grayscale' : ''}>
        {m.announcement && <p className="text-[11px] font-bold text-amber-700 mb-0.5 flex items-center gap-1"><Megaphone className="w-3 h-3" />Aviso · {m.sender_name}</p>}
        {!mine && !m.announcement && <p className="text-[11px] font-semibold text-[#0e7490] mb-0.5">{m.sender_name}</p>}
        <UrgencyBadge m={m} canResolve={canResolve} onChanged={onChanged} />
        {m.reply_to && <Quote r={m.reply_to} />}
        {m.ref && (
          <Link to={m.ref.url || '#'} className={`flex items-center gap-1 text-xs underline mb-1 ${mine ? 'text-white/90' : 'text-[#2D3E6F]'}`} data-testid={`chat-msg-ref-${m.id}`}>
            <Link2 className="w-3 h-3" />{m.ref.type === 'lead' ? 'Lead' : 'Contrato'}: {m.ref.label}
          </Link>
        )}
        {m.file && (
          m.file.content_type?.startsWith('audio/')
            ? <span className="flex items-center gap-1.5 mb-1"><audio controls preload="metadata" src={fileUrl(m.file.id)} className="w-56 max-w-full h-10" data-testid={`chat-msg-audio-${m.id}`} />{m.file.duration > 0 && <span className={`text-[11px] tabular-nums ${mine ? 'text-white/70' : 'text-slate-500'}`} data-testid={`chat-msg-audio-duration-${m.id}`}>{Math.floor(m.file.duration / 60)}:{String(m.file.duration % 60).padStart(2, '0')}</span>}</span>
            : m.file.content_type?.startsWith('image/')
            ? <a href={fileUrl(m.file.id)} target="_blank" rel="noreferrer"><img src={fileUrl(m.file.id)} alt={m.file.name} className="rounded-lg max-h-56 mb-1" /></a>
            : <a href={fileUrl(m.file.id)} target="_blank" rel="noreferrer" className={`flex items-center gap-1 underline mb-1 ${mine ? 'text-white' : 'text-[#2D3E6F]'}`}><FileText className="w-4 h-4" />{m.file.name}</a>
        )}
        {m.file && onUseFile && !m.file.content_type?.startsWith('audio/') && (
          <button type="button" onClick={() => onUseFile(m.file)} className={`flex items-center gap-1 text-[11px] font-semibold rounded-full px-2 py-0.5 mb-1 ${mine ? 'bg-white/15 text-white hover:bg-white/25' : 'bg-sky-50 text-sky-800 hover:bg-sky-100'}`} data-testid={`chat-use-file-${m.id}`}>
            <FilePlus2 className="w-3 h-3" />Usar num contrato
          </button>
        )}
        {m.text && (onlyEmojis(m.text)
          ? <p className="leading-tight py-0.5" data-testid={`chat-msg-jumbo-${m.id}`}><EmojiText text={m.text} size="2.6em" /></p>
          : <p className="whitespace-pre-wrap break-words"><EmojiText text={m.text} /></p>)}
        {m.edit_history?.length > 0 && <EditHistory m={m} mine={mine && !m.announcement} />}
        <p className={`text-[10px] mt-0.5 text-right ${mine && !m.announcement ? 'text-white/60' : 'text-slate-400'}`}>{m.edited_at && <span className="italic mr-1" data-testid={`chat-msg-edited-${m.id}`}>editada ·</span>}{fmtTime(m.created_at)}</p>
        {m.announcement && <AnnouncementFooter m={m} me={me} canSeeAcks={canSeeAcks} onShowAcks={onShowAcks} onChanged={onChanged} />}
        </div>
        </div>
        {!mine && <Actions m={m} onReply={canReact ? onReply : null} onReact={canReact ? () => setBarOpen((o) => !o) : null} />}
      </div>
      <ReactionChips m={m} me={me} names={names || {}} mine={mine} canReact={canReact} onChanged={onChanged} />
    </div>
  );
};
